import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { productsAPI, dipAPI } from '@/utils/api';
import { Download, CheckCircle2, Circle, AlertTriangle, FileText, Loader2, FolderDown, FileDown, Package, ShieldCheck, Beaker, Award } from 'lucide-react';
import { toast } from 'sonner';
import DipBreadcrumb from '@/components/DipBreadcrumb';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DIPGeneration = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [dipStatus, setDipStatus] = useState(null);
  const [annexes, setAnnexes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadData();
  }, [productId]);

  const loadData = async () => {
    try {
      const prodRes = await productsAPI.getOne(productId);
      setProduct(prodRes.data);

      const statusRes = await dipAPI.getStatus(productId);
      setDipStatus(statusRes.data);

      try {
        const annexesRes = await fetch(`${API_BASE}/api/products/${productId}/annexes/list`);
        if (annexesRes.ok) setAnnexes(await annexesRes.json());
      } catch (e) {}
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // DIP can be generated as soon as Formula + Manufacturing + Toxicology + Labels are complete.
  // Certificates are OPTIONAL.
  const canGenerateDIP = dipStatus?.has_formula && dipStatus?.has_manufacturing && dipStatus?.has_toxicology && dipStatus?.has_labels;

  const missingRequiredSteps = [
    !dipStatus?.has_formula && { key: 'formula',           label: 'Formule cosmétique',     route: 'formula' },
    !dipStatus?.has_manufacturing && { key: 'manufacturing', label: 'Méthode de fabrication', route: 'manufacturing' },
    !dipStatus?.has_toxicology && { key: 'toxicology',       label: 'Évaluation toxicologique', route: 'toxicology' },
    !dipStatus?.has_labels && { key: 'labels',               label: 'Étiquettes', route: 'labels' },
  ].filter(Boolean);

  const handleGenerateDIP = async () => {
    if (!canGenerateDIP) {
      toast.error('Complétez Formule, Fabrication, Toxicologie et Étiquettes avant de générer le DIP');
      return;
    }
    setGenerating(true);
    try {
      const res = await dipAPI.generate(productId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DIP_${product.name.replace(/ /g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('DIP généré avec succès');
      loadData();
    } catch (error) {
      console.error('Error generating DIP:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération du DIP');
    } finally {
      setGenerating(false);
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await fetch(`${API_BASE}${url}`);
      if (!res.ok) { toast.error('Document non disponible'); return; }
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl; a.download = filename || 'document'; a.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) { toast.error('Erreur de téléchargement'); }
  };

  const downloadAnnexesZip = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/products/${productId}/annexes/download`);
      if (!res.ok) { toast.error('Aucune annexe disponible'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Annexes_DIP_${product?.name?.replace(/ /g, '_')}.zip`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Annexes téléchargées (ZIP)');
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton-shimmer h-64 rounded-lg"></div>
      </div>
    );
  }

  const totalAnnexes =
    (annexes?.labels?.length || 0) +
    (annexes?.fds?.length || 0) +
    (annexes?.toxicology ? 1 : 0) +
    (annexes?.certificates?.length || 0);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <DipBreadcrumb productId={productId} productName={product?.name} currentStep="dip" />

      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">
          Génération du DIP
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {product?.name} — Compilation et export du Dossier d'Information Produit
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Progression du DIP</CardTitle>
          <CardDescription>Formule, Fabrication, Toxicologie et Étiquettes sont obligatoires. Les certificats sont optionnels.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Complétion globale</span>
              <span className="font-semibold text-foreground">{dipStatus?.completion_percentage || 0}%</span>
            </div>
            <Progress value={dipStatus?.completion_percentage || 0} className="h-3" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ModuleStatus done={dipStatus?.has_formula} label="Formule cosmétique" desc="Composition INCI et vérification" required />
            <ModuleStatus done={dipStatus?.has_manufacturing} label="Méthode de fabrication" desc="Procédé BPF ISO 22716" required />
            <ModuleStatus done={dipStatus?.has_toxicology} label="Évaluation toxicologique" desc="Rapport du toxicologue" required />
            <ModuleStatus done={dipStatus?.has_labels} label="Étiquettes" desc="Au moins une étiquette du produit" required />
            <ModuleStatus done={dipStatus?.has_certificates} label="Certificats & Justificatifs" desc="Optionnel — facultatifs" required={false} />
          </div>
        </CardContent>
      </Card>

      {canGenerateDIP ? (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle2 size={24} />
              DIP prêt à être généré
            </CardTitle>
            <CardDescription className="text-green-700">
              Le DIP complet (avec annexes intégrées) ou les documents séparés peuvent être téléchargés ci-dessous.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              data-testid="btn-generate-dip"
              onClick={handleGenerateDIP}
              disabled={generating}
              className="w-full gap-2 h-12 text-base"
            >
              {generating ? (
                <><Loader2 className="animate-spin" size={20} />Génération en cours...</>
              ) : (
                <><Download size={20} />Télécharger le DIP complet (PDF avec annexes intégrées)</>
              )}
            </Button>
            <Button
              data-testid="btn-download-annexes-zip"
              variant="outline"
              className="w-full gap-3 h-12"
              onClick={downloadAnnexesZip}
            >
              <FolderDown size={20} />
              Télécharger toutes les annexes en ZIP ({totalAnnexes} fichier{totalAnnexes > 1 ? 's' : ''})
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle size={24} />
              Étapes obligatoires manquantes
            </CardTitle>
            <CardDescription className="text-amber-700">
              Veuillez compléter Formule, Fabrication, Toxicologie et Étiquettes pour générer le DIP. Les certificats restent optionnels.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {missingRequiredSteps.map(step => (
              <Button
                key={step.key}
                variant="outline"
                onClick={() => navigate(`/product/${productId}/${step.route}`)}
                className="w-full justify-start gap-3"
                data-testid={`btn-complete-${step.key}`}
              >
                <Circle size={18} />
                Compléter : {step.label}
              </Button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Téléchargement individuel des documents */}
      {annexes && totalAnnexes > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileDown size={20} />
              Télécharger les documents individuellement
            </CardTitle>
            <CardDescription>
              Si vous préférez récupérer chaque pièce séparément plutôt que dans le PDF unique.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {annexes.toxicology && (
              <DocSection icon={ShieldCheck} title="Rapport toxicologique" color="text-purple-700">
                <DocLine
                  filename={annexes.toxicology.filename}
                  meta={annexes.toxicology.toxicologist_name ? `Toxicologue : ${annexes.toxicology.toxicologist_name}` : ''}
                  onClick={() => downloadFile(annexes.toxicology.download_url, annexes.toxicology.filename)}
                  testId="dl-toxicology"
                />
              </DocSection>
            )}

            {annexes.fds.length > 0 && (
              <DocSection icon={Beaker} title={`Fiches FDS — ingrédients (${annexes.fds.length})`} color="text-blue-700">
                {annexes.fds.map((f, i) => (
                  <DocLine
                    key={f.id}
                    filename={f.filename}
                    meta={f.material_name}
                    onClick={() => downloadFile(f.download_url, f.filename)}
                    testId={`dl-fds-${i}`}
                  />
                ))}
              </DocSection>
            )}

            {annexes.labels.length > 0 && (
              <DocSection icon={Package} title={`Étiquettes (${annexes.labels.length})`} color="text-orange-700">
                {annexes.labels.map((l, i) => (
                  <DocLine
                    key={l.id}
                    filename={l.filename}
                    onClick={() => downloadFile(l.download_url, l.filename)}
                    testId={`dl-label-${i}`}
                  />
                ))}
              </DocSection>
            )}

            {annexes.certificates.length > 0 && (
              <DocSection icon={Award} title={`Certificats (${annexes.certificates.length})`} color="text-emerald-700">
                {annexes.certificates.map((c, i) => (
                  <DocLine
                    key={c.id}
                    filename={c.filename}
                    meta={c.certificate_type}
                    onClick={() => downloadFile(c.download_url, c.filename)}
                    testId={`dl-cert-${i}`}
                  />
                ))}
              </DocSection>
            )}
          </CardContent>
        </Card>
      )}

      <div className="p-6 bg-muted rounded-lg">
        <div className="flex items-start gap-3">
          <FileText className="text-primary mt-1" size={24} strokeWidth={1.5} />
          <div>
            <h3 className="font-semibold text-foreground mb-2">Qu'est-ce qu'un DIP ?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              Le Dossier d'Information Produit (DIP) est un document obligatoire selon le règlement européen CE
              1223/2009. Il compile toutes les informations sur la composition, la fabrication et la sécurité de votre
              produit cosmétique.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Le DIP doit être conservé et tenu à la disposition des autorités compétentes pendant au moins 10 ans après
              la mise sur le marché du dernier lot du produit.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModuleStatus = ({ done, label, desc, required }) => (
  <div
    className={`flex items-start gap-3 p-4 border rounded-md transition-colors ${
      done ? 'border-green-200 bg-green-50' : required ? 'border-amber-200 bg-amber-50/40' : 'border-border bg-card'
    }`}
  >
    {done ? <CheckCircle2 className="text-green-600 mt-0.5" size={22} /> : <Circle className={`mt-0.5 ${required ? 'text-amber-500' : 'text-muted-foreground'}`} size={22} />}
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <p className="font-medium text-foreground">{label}</p>
        {!required && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">Optionnel</span>}
      </div>
      <p className="text-sm text-muted-foreground">{desc}</p>
    </div>
  </div>
);

const DocSection = ({ icon: Icon, title, color, children }) => (
  <div>
    <div className={`flex items-center gap-2 mb-2 ${color}`}>
      <Icon size={16} />
      <h4 className="text-sm font-semibold uppercase tracking-wider">{title}</h4>
    </div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const DocLine = ({ filename, meta, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-card hover:border-[#205F79] hover:bg-[#205F79]/5 transition-all text-left group"
  >
    <FileText size={16} className="text-muted-foreground shrink-0 group-hover:text-[#205F79]" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground truncate">{filename}</p>
      {meta && <p className="text-xs text-muted-foreground truncate">{meta}</p>}
    </div>
    <Download size={15} className="text-muted-foreground shrink-0 group-hover:text-[#205F79]" />
  </button>
);

export default DIPGeneration;
